import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaSun, FaMoon, FaUser, FaChevronDown, FaSignInAlt, FaUserPlus, FaSignOutAlt, FaGlobe } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePriestAuth } from '../context/PriestAuthContext';

// Primary nav items - visible in navbar
const primaryNavItems = [
  { path: '/', key: 'navHome', icon: '🔯' },
  { path: '/horoscope', key: 'navHoroscope', icon: '🪷' },
  { path: '/kundali', key: 'navKundali', icon: '🪬' },
  { path: '/compatibility', key: 'navCompatibility', icon: '💕' },
  { path: '/tarot', key: 'navTarot', icon: '🃏' },
  { path: '/ask', key: 'navAskAI', icon: '✨' },
];

// Secondary nav items - in dropdown
const secondaryNavItems = [
  { path: '/moon', key: 'navMoonPhase', icon: '🌙' },
  { path: '/nakshatra', key: 'navNakshatra', icon: '⭐' },
  { path: '/muhurat', key: 'navMuhurat', icon: '📅' },
  { path: '/numerology', key: 'navNumerology', icon: '🔢' },
  { path: '/vedic', key: 'navVedic', icon: '🕉️' },
  { path: '/remedies', key: 'navRemedies', icon: '🙏' },
  { path: '/palm', key: 'navPalmReading', icon: '🖐️' },
  { path: '/temples', key: 'navTemples', icon: '🛕' },
];

// All items for mobile menu
const allNavItems = [...primaryNavItems, ...secondaryNavItems];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const dropdownRef = useRef(null);
  const langRef = useRef(null);
  const location = useLocation();
  const { language, setLanguage, t, languages } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const { priest } = usePriestAuth();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if any secondary item is active
  const isSecondaryActive = secondaryNavItems.some(item => location.pathname === item.path);

  const currentLang = languages.find(l => l.code === language) || languages[0];

  return (
    <nav className="navbar">
      <Link to="/" className="navbar-logo">
        <span className="logo-icon">🕉️</span>
        <h1>AstroA2Z</h1>
      </Link>

      <div className="navbar-right">
        <button
          className="theme-toggle"
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? <FaSun /> : <FaMoon />}
        </button>

        <div className="lang-selector" ref={langRef}>
          <button
            className="lang-toggle"
            onClick={() => setIsLangOpen(!isLangOpen)}
            title="Change language"
          >
            <FaGlobe style={{ marginRight: '4px' }} />
            {currentLang.short}
            <FaChevronDown className={`dropdown-arrow ${isLangOpen ? 'open' : ''}`} style={{ marginLeft: '4px', fontSize: '0.7rem' }} />
          </button>
          {isLangOpen && (
            <ul className="lang-dropdown-menu">
              {languages.map(lang => (
                <li key={lang.code}>
                  <button
                    className={`lang-option ${lang.code === language ? 'active' : ''}`}
                    onClick={() => {
                      setLanguage(lang.code);
                      setIsLangOpen(false);
                    }}
                  >
                    <span className="lang-short">{lang.short}</span>
                    <span className="lang-label">{lang.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {user ? (
          <>
            <Link to="/profile" className="profile-btn" title="My Profile">
              <FaUser />
            </Link>
            <button
              className="auth-nav-btn"
              onClick={logout}
              title={`Sign out (${user.name || user.email})`}
            >
              <FaSignOutAlt />
              <span className="auth-nav-label">Sign Out</span>
            </button>
          </>
        ) : (
          <Link to="/login" className="auth-nav-btn" title="Sign In">
            <FaUser />
          </Link>
        )}

        <Link
          to={priest ? '/priest/dashboard' : '/priest/login'}
          className="auth-nav-btn priest-nav-btn"
          title={priest ? 'Purohit Dashboard' : 'Purohit Login'}
          style={{ background: 'linear-gradient(135deg,#ff9933,#d4a017)', color: '#1a0f00' }}
        >
          <span style={{ fontSize: '1.1rem', lineHeight: 1 }}>🕉️</span>
          <span className="auth-nav-label">Purohit</span>
        </Link>

        <button className="mobile-menu-btn" onClick={() => setIsOpen(!isOpen)}>
          {isOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      <ul className={`navbar-links ${isOpen ? 'open' : ''}`}>
        {/* Desktop: Primary items */}
        {primaryNavItems.map(item => (
          <li key={item.path} className="nav-item-desktop">
            <Link
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              <span>{item.icon}</span>
              {t(item.key)}
            </Link>
          </li>
        ))}

        {/* Desktop: More dropdown */}
        <li className="nav-dropdown-desktop" ref={dropdownRef}>
          <button
            className={`nav-dropdown-btn ${isSecondaryActive ? 'active' : ''}`}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span>📋</span>
            {t('navMore') || 'More'}
            <FaChevronDown className={`dropdown-arrow ${isDropdownOpen ? 'open' : ''}`} />
          </button>
          {isDropdownOpen && (
            <ul className="nav-dropdown-menu">
              {secondaryNavItems.map(item => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={location.pathname === item.path ? 'active' : ''}
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setIsOpen(false);
                    }}
                  >
                    <span>{item.icon}</span>
                    {t(item.key)}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>

        {/* Mobile: All items */}
        {allNavItems.map(item => (
          <li key={`mobile-${item.path}`} className="nav-item-mobile">
            <Link
              to={item.path}
              className={location.pathname === item.path ? 'active' : ''}
              onClick={() => setIsOpen(false)}
            >
              <span>{item.icon}</span>
              {t(item.key)}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
